import Result from 'true-myth/result';
import type { Except } from 'type-fest';
import type { ZodPipe, ZodTypeAny } from 'zod';

import type {
    HttpFetcher,
    HttpFetcherError,
    ParametersWithPathname,
    ParametersWithUrl,
    RequestHeaders,
} from '../http-fetcher/http-fetcher';
import { isParametersWithUrl } from '../http-fetcher/http-fetcher';

export type ErrorReporter = {
    readonly reportError: (message: string, error: Error) => void;
};

type AnySchema = ZodPipe<ZodTypeAny> | ZodTypeAny;

type ValidationResult<SchemaType extends AnySchema> = Result<SchemaType['_output'], HttpFetcherError>;

type ValidationResultWithStatus<SchemaType extends AnySchema> = Result<
    { readonly data: SchemaType['_output']; readonly status: number },
    HttpFetcherError
>;

type MethodExtendedWithValidation<T> = T extends (...params: infer U) => unknown
    ? <SchemaType extends AnySchema>(
          validationSchema: SchemaType,
          ...params: U
      ) => Promise<ValidationResult<SchemaType>>
    : never;

type MethodExtendedWithValidationAndStatus<T> = T extends (...params: infer U) => unknown
    ? <SchemaType extends AnySchema>(
          validationSchema: SchemaType,
          ...params: U
      ) => Promise<ValidationResultWithStatus<SchemaType>>
    : never;

export interface ValidatingHttpFetcher<T extends HttpFetcher = HttpFetcher>
    extends Except<
        HttpFetcher,
        'deleteJson' | 'get' | 'getWithStatus' | 'patchJson' | 'post' | 'postJson' | 'put' | 'putJson'
    > {
    readonly deleteJson: MethodExtendedWithValidation<T['deleteJson']>;
    readonly get: MethodExtendedWithValidation<T['get']>;
    readonly getWithStatus: MethodExtendedWithValidationAndStatus<T['getWithStatus']>;
    readonly patchJson: MethodExtendedWithValidation<T['patchJson']>;
    readonly post: MethodExtendedWithValidation<T['post']>;
    readonly postJson: MethodExtendedWithValidation<T['postJson']>;
    readonly put: MethodExtendedWithValidation<T['put']>;
    readonly putJson: MethodExtendedWithValidation<T['putJson']>;
}

type ValidatingHttpFetcherDependencies<T> = {
    readonly errorReporter: ErrorReporter;
    readonly httpFetcher: T;
};

const reportSchemaError = (
    errorReporter: ErrorReporter,
    method: string,
    parameters: ParametersWithPathname | ParametersWithUrl,
    error: Error,
): void => {
    try {
        const path = isParametersWithUrl(parameters) ? parameters.url.toString() : parameters.pathname.toString();

        errorReporter.reportError(`Schema validation failed for ${method} ${path}`, error);
        // biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration
    } catch (_) {}
};

export const createValidatingHttpFetcher = <T extends HttpFetcher>({
    errorReporter,
    httpFetcher,
}: ValidatingHttpFetcherDependencies<T>): ValidatingHttpFetcher => {
    const validateResult = <SchemaType extends AnySchema>(
        parameters: ParametersWithPathname | ParametersWithUrl,
        method: string,
        validationSchema: SchemaType,
        result: Result<unknown, Error>,
    ): ValidationResult<SchemaType> => {
        if (result.isOk) {
            const validationResult = validationSchema.safeParse(result.value);

            if (validationResult.success === true) {
                return Result.ok(validationResult.data);
            }

            reportSchemaError(errorReporter, method, parameters, validationResult.error);

            return Result.err(validationResult.error);
        }

        return Result.err(result.error);
    };

    return {
        addHttpErrorHandler: httpFetcher.addHttpErrorHandler,
        delete: httpFetcher.delete,
        async deleteJson(validationSchema, parameters) {
            const result = await httpFetcher.deleteJson(parameters);

            return validateResult(parameters, 'deleteJson', validationSchema, result);
        },
        async get(validationSchema, parameters) {
            const result = await httpFetcher.get(parameters);

            return validateResult(parameters, 'get', validationSchema, result);
        },
        async getWithStatus(validationSchema, parameters) {
            const result = await httpFetcher.getWithStatus(parameters);

            if (result.isErr) {
                return Result.err(result.error);
            }

            const { data, status } = result.value;
            const validated = validateResult(parameters, 'getWithStatus', validationSchema, Result.ok(data));

            if (validated.isErr) {
                return Result.err(validated.error);
            }

            return Result.ok({ data: validated.value, status });
        },
        async patchJson(validationSchema, parameters) {
            const result = await httpFetcher.patchJson(parameters);

            return validateResult(parameters, 'patchJson', validationSchema, result);
        },
        async post(validationSchema, parameters) {
            const result = await httpFetcher.post(parameters);

            return validateResult(parameters, 'post', validationSchema, result);
        },
        async postJson(validationSchema, parameters) {
            const result = await httpFetcher.postJson(parameters);

            return validateResult(parameters, 'postJson', validationSchema, result);
        },
        async put(validationSchema, parameters) {
            const result = await httpFetcher.put(parameters);

            return validateResult(parameters, 'put', validationSchema, result);
        },
        async putJson(validationSchema, parameters) {
            const result = await httpFetcher.putJson(parameters);

            return validateResult(parameters, 'putJson', validationSchema, result);
        },
        set requestHeaders(_requestHeaders: RequestHeaders) {
            httpFetcher.requestHeaders = _requestHeaders;
        },
    };
};
